import { cn } from "@/lib/utils";
import { PAGE_WIDTHS, type PageWidth } from "@/lib/page-width";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useCompanyRedirect } from "@/features/clients/hooks/use-company-redirect";
import { useLaunchOnboardingForOwners } from "@/features/onboarding/hooks/use-onboarding";
import { useTitle } from "@/features/utils/use-title";
import { ArrowLeft } from "@phosphor-icons/react";
import { Outlet, useNavigate } from "react-router-dom";
import { BackofficeSidebar } from "./sidebar";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { externalAccountKeys } from "@/features/external-accounts/hooks/use-external-accounts";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { setCurrentCompany } from "@/features/analytics/posthog";
import { useClientSocket } from "@/features/websockets/use-user-socket";
import { ResumeOnboardingBanner } from "@/views/onboarding/resume-banner";
import { WelcomeDialog } from "@/views/onboarding/welcome-dialog";

export const BackofficeLayout = () => {
  // This hook handles automatic redirects based on company state
  useCompanyRedirect();

  // A tenant whose guided setup has never been run (or has been reset) gets it
  // now, before anything else — it is the one thing that configures the rest.
  useLaunchOnboardingForOwners();

  const queryClient = useQueryClient();
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  // Live updates of every list (call history, contacts…) for this company.
  useClientSocket();

  useEffect(() => {
    if (currentClient?.client) setCurrentCompany(currentClient.client);
  }, [clientId]);

  // Global OAuth callback listener (stays active while in backoffice)
  useEffect(() => {
    console.log("[BackofficeLayout] Setting up OAuth message listener");

    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === "oauth-complete") {
        console.log("[BackofficeLayout] OAuth complete detected");

        if (event.data?.success) {
          console.log(
            "[BackofficeLayout] OAuth success! Storing flag and refreshing accounts",
          );

          // Store success flag for AccountsListPage to pick up
          localStorage.setItem("oauth-success-pending", "true");

          // Refresh accounts list
          if (clientId) {
            queryClient.invalidateQueries({
              queryKey: externalAccountKeys.list(clientId),
            });
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [queryClient, clientId]);

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 56)",
          "--header-height": "calc(var(--spacing) * 12)",
        } as React.CSSProperties
      }
    >
      <BackofficeSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="px-4 md:px-8">
              <ResumeOnboardingBanner />
              <WelcomeDialog />
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export function SiteHeader() {
  const title = useTitle();
  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height) md:hidden">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-2 data-[orientation=vertical]:h-4"
        />
        <h1 className="text-base font-medium">{title}</h1>
      </div>
    </header>
  );
}

export function PageLayout({
  title,
  titleSuffix,
  description,
  actions,
  backTo,
  width = "default",
  children,
}: {
  title: string;
  /** Drawn after the heading only, not in the tab title (the "?" of an unsure name). */
  titleSuffix?: React.ReactNode;
  description?: string;
  actions?: React.ReactNode;
  backTo?: { label: string; path: string };
  /** Which one is the kind of page, never taste: see `lib/page-width.ts`. */
  width?: PageWidth;
  children: React.ReactNode;
}) {
  useTitle(title);
  const navigate = useNavigate();

  return (
    <div className={cn("space-y-6 pt-8 pb-16 mx-auto", PAGE_WIDTHS[width])}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          {backTo && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(backTo.path)}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {backTo.label}
            </Button>
          )}
          <h2 className="text-2xl font-semibold tracking-tight">
            {title}
            {titleSuffix}
          </h2>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>

      {children}
    </div>
  );
}
